import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, Component, Suspense, lazy } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Box,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import EditIcon from "@mui/icons-material/Edit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import "./StandardFormView.css";
import { styled } from "@mui/system";
import { GetFrameworkData } from "../../../Api/Framework";
import { GetControlDataByOrgIdFrameworkId } from "../../../Api/Control";
import { hasTechAdminAccess, hasConsultantAccess, isAdminUser, hasMenuPermission } from "../../../Utils/adminUserTypes";
import azureAiService from "../../../services/azureAiService";
import useAISettings from "../../../Utils/useAISettings";

// PERFORMANCE OPTIMIZATION: Lazy load all modals
// Modals are large components (2,000-6,000 lines) that shouldn't be loaded until opened
// This reduces initial bundle size by ~1-2MB

const CountryModal = lazy(() => import("../../Country/CountryModal"));
const AssetManagementModal = lazy(() => import("../../AssetManagement/AssetManagementModal"));
const GroupListModal = lazy(() => import("../../Groups/GroupListModal"));
const GroupTypeModal = lazy(() => import("../../Groups/GroupTypeModal"));
const OrganizationModal = lazy(() => import("../../OrganizationList/OrganizationModal"));
const FrameworkModal = lazy(() => import("../../Framework/FrameworkModal"));
const ControlModal = lazy(() => import("../../Control/ControlModal")); // 5,883 lines!
const VendorModal = lazy(() => import("../../Vendor/VendorModal"));
const VendorAccountModal = lazy(() => import("../../VendorAccount/VendorAccountModal"));
const DataInventoryModal = lazy(() => import("../../DataInventory/DataInventoryModal"));
const PolicyCenterModal = lazy(() => import("../../PolicyCenter/PolicyCenterModal"));
const PolicyModal = lazy(() => import("../../Policy/PolicyModal"));
const UsersModal = lazy(() => import("../../Users/UsersModal"));
const ProjectModal = lazy(() => import("../../Projects/ProjectModal")); // 3,280 lines!
const FormStandardModal = lazy(() => import("../../FormBuilder/FormStandardModal"));
const TrainingModal = lazy(() => import("../../Training/TrainingModal"));
const TrainingAttendanceModal = lazy(() => import("../../TrainingAttendance/TrainingAttendanceModal"));
const PolicyTemplateModal = lazy(() => import("../../PolicyTemplate/PolicyTemplateModal"));
const ControlImplementationTemplateModal = lazy(() => import("../../ControlImplementationTemplate/ControlImplementationTemplateModal"));
const FrameworkOrganizationModal = lazy(() => import("../../FrameworkOrganization/FrameworkOrganizationModal"));
const RiskAssessmentModal = lazy(() => import("../../RiskAssessment/RiskAssessmentModal"));
const RiskRemediationModal = lazy(() => import("../../RiskRemediation/RiskRemediationModal"));
const RiskModal = lazy(() => import("../../Risk/RiskModal"));
const ResourceDefaultsModal = lazy(() => import("../../SystemDefaultFields/ResourceDefaultsModal"));
const ChatbotAgent = lazy(() => import("../../ChatbotAgent/ChatbotAgent"));
const IPRegisterFormModal = lazy(() => import("../../IPRegister/IPRegisterFormModal"));
const GrantEntitlementModal = lazy(() => import("../../BillingAdmin/GrantEntitlementModal"));
const EntitlementCodeModal = lazy(() => import("../../BillingAdmin/EntitlementCodeModal"));
const ReviewWithZavvyModal = lazy(() => import("../../PolicyCenter/ReviewWithZavvyModal"));
const KnowledgeLibraryModal = lazy(() => import("../../KnowledgeLibrary/KnowledgeLibraryModal"));

// Modal loading fallback
const ModalLoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 200,
      padding: 3,
    }}
  >
    <CircularProgress size={40} />
  </Box>
);

/**
 * ErrorBoundary component to catch and handle errors in child components
 * Prevents the entire dialog from crashing when ChatbotAgent fails
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error details for debugging
    console.error('ChatbotAgent Error Boundary caught an error:', error, errorInfo);
    
    // Call onError handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Return fallback UI - minimal and non-blocking
      return this.props.fallback || (
        <Box sx={{ 
          p: 1, 
          textAlign: 'center',
          color: '#6B7280',
          fontSize: '12px'
        }}>
          AI Assistant temporarily unavailable
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * StandardFormView Component
 * 
 * A flexible modal component that supports two distinct layout modes for different use cases:
 * 
 * ================================================================================
 * BASIC MODE (layoutMode="basic") - Default for all standard forms
 * ================================================================================
 * 
 * Purpose: Traditional form editing for simple entities
 * Used For: Country, Organization, Policy, Vendor, User, Project, Training, etc.
 * 
 * Features:
 * - Layout: 70% viewport width, auto height with max-width of 600px
 * - Content: Centered form fields with natural scrolling
 * - Edit Mode: Single edit mode - all fields become editable when Edit is clicked
 * - Buttons: Simple Edit/Save/Cancel workflow
 * - Background: White dialog background
 * - Scrolling: Dialog-level scrolling for entire content
 * 
 * Edit Flow:
 * 1. View mode: All fields read-only
 * 2. Click Edit: All fields become editable simultaneously
 * 3. Click Save: Save all changes
 * 4. Click Cancel: Discard changes, return to view mode
 * 
 * ================================================================================
 * EXTENDED MODE (layoutMode="extended") - Specialized for Control operations
 * ================================================================================
 * 
 * Purpose: Complex multi-section editing with granular permissions
 * Used For: ALL Control operations (view/edit/add) for consistent UX
 * 
 * Features:
 * - Layout: 85% viewport width, 90% viewport height (full-screen experience)
 * - Content: 3-column expandable interface (Control Details | Implementation | Attachments)
 * - Edit Mode: Multiple edit modes with section-based permissions
 * - Buttons: Edit All (Tech Admin) + Edit (All Users) with different capabilities
 * - Background: Gray background (#F9FAFB) with framework header
 * - Scrolling: Individual column scrolling, no dialog scrolling
 * - Navigation: Previous/Next control navigation with progress tracking
 * 
 * Edit Modes:
 * 
 * 1. EDIT ALL MODE (Tech Admin Only):
 *    - Flag: "editall"
 *    - Permissions: Can edit Control Details + Implementation + Attachments
 *    - Control Details: Editable (framework-level data)
 *    - Implementation: Editable (organization-specific data)
 *    - Attachments: Editable (organization-specific data)
 *    - Use Case: Framework maintenance and standardization
 * 
 * 2. EDIT MODE (All Users):
 *    - Flag: "edit_user_implement"
 *    - Permissions: Can edit Implementation + Attachments only
 *    - Control Details: Read-only (custom form view with styled boxes)
 *    - Implementation: Editable (organization-specific data)
 *    - Attachments: Editable (organization-specific data)
 *    - Use Case: Organization-specific implementation customization
 * 
 * 3. VIEW MODE:
 *    - Flag: "view"
 *    - Permissions: Read-only access to all sections
 *    - Control Details: Read-only (custom form view)
 *    - Implementation: Read-only
 *    - Attachments: Read-only
 *    - Use Case: Information review and navigation
 * 
 * Security Model:
 * - Control Details contain framework-level standardized information
 * - Only Tech Admin can modify Control Details to maintain system-wide consistency
 * - All users can modify Implementation and Attachments for their organization
 * - Framework integrity is protected while allowing organizational customization
 * 
 * ================================================================================
 * COMPARISON TABLE: Basic vs Extended Mode
 * ================================================================================
 * 
 * | Feature                    | Basic Mode           | Extended Mode         |
 * |----------------------------|---------------------|----------------------|
 * | Layout Size               | 70% width, auto     | 85% width, 90% height|
 * | Content Structure         | Single form         | 3-column expandable  |
 * | Edit Modes                | 1 mode (all fields) | 3 modes (sectioned)  |
 * | Edit Buttons              | 1 button (Edit)     | 2 buttons (Edit All + Edit) |
 * | Permission Model          | Role-based access   | Section-based + role-based |
 * | Scrolling                 | Dialog-level        | Column-level         |
 * | Background                | White dialog        | Gray with header     |
 * | Navigation                | None                | Previous/Next controls |
 * | Progress Tracking         | None                | Framework progress   |
 * | Use Cases                 | Simple entities     | Complex Control management |
 * | Framework Integration     | None                | Full framework context |
 * 
 * Usage Guidelines:
 * - Default: layoutMode="basic" (all forms except Control modals)
 * - Control Modals: layoutMode="extended" (all Control operations for consistency)
 * - Other Forms: layoutMode="basic" (centered form fields)
 */
const StandardFormView = ({
  open,
  onClose,
  title,
  subTitle,
  flag,
  getCountryAllData,
  editItems,
  isPage,
  fetchGroupData,
  groupTypes,
  getAssetManagementAllData,
  currentActiveTab,
  GetVendorAllData,
  vendorAccount,
  vendorNames,
  fullUserNames,
  getAllData,
  selectedOrg,
  setUpdatePinBarData,
  fetchOrgData,
  frameWorkId,
  getActiveTabID,
  onEdit,
  onEditAll,
  navigateToControl,
  controlsListData = [], // Pass current control data for navigation
  onSaveComplete = null, // Callback to reset flag after save
  onDataRefresh = null, // Callback to refresh current control data after save
  layoutMode = 'basic', // 'basic' or 'extended' - see component documentation above
  entityType = null, // For system default fields modal
  fieldName = null, // For system default fields modal
  organizationScope = null, // For system default fields modal
  existingSoftwareAssets = [], // Pass existing software assets for frontend validation
  extraBottomContent = null, // Optional content rendered at the bottom of DialogContent
  ipRegisterConfig = null, // For ip_register modal: the entity config object
  onSave = null, // For ip_register modal: async (payload) => void
  relatedModelType = null, // For policy center modal: link doc to an entity (related_model_type)
  relatedModelId = null, // For policy center modal: the linked entity's UUID (related_model_id)
  userList = [], // For risk_remediation modal: users available for the Treatment Owner select
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [frameworkData, setFrameworkData] = useState(null);
  const [frameworkStats, setFrameworkStats] = useState({
    total: 0,
    implemented: 0,
    percentage: 0
  });
  // Store full list of controls for computing previous/next navigation
  const [controlsList, setControlsList] = useState([]);
  const OrganizationId = localStorage.getItem("organizationId");
  
  // Chatbot state
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [showAIColumn, setShowAIColumn] = useState(false);
  
  // Resend email state
  const [resending, setResending] = useState(false);
  
  // AI Settings hook for global AI visibility control
  const { aiEnabled } = useAISettings();
  
  const childRef = useRef();

  // Fetch framework metadata once when the modal opens or the framework changes.
  // Intentionally excludes controlsListData — stats are recomputed in the effect below
  // without triggering a new GetFrameworkData() call on every list update.
  useEffect(() => {
    const fetchFrameworkData = async () => {
      if (isPage !== "control" || !frameWorkId) return;
      try {
        const frameworkResponse = await GetFrameworkData();
        const framework = frameworkResponse?.data?.find(f => f.id === frameWorkId);
        setFrameworkData(framework);
      } catch (error) {
        console.error("Error fetching framework data:", error);
      }
    };
    fetchFrameworkData();
  }, [isPage, frameWorkId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute framework stats and navigation list whenever the control list changes.
  // Uses the already-fetched controlsListData from the parent — no extra API call needed.
  useEffect(() => {
    const computeStats = async () => {
      if (isPage !== "control" || !frameWorkId) return;
      try {
        let controls = [];
        if (controlsListData && controlsListData.length > 0) {
          controls = controlsListData;
        } else {
          // Fallback only when the parent hasn't passed data yet (initial load)
          const controlResponse = await GetControlDataByOrgIdFrameworkId(frameWorkId, OrganizationId, "");
          if (controlResponse?.status === 200) {
            controls = controlResponse.data || [];
          }
        }

        if (controls.length > 0) {
          const totalControls = controls.length;
          const implementedControls = controls.filter(control =>
            control.framework_control_organization?.[0]?.fco_status === "Yes"
          ).length;
          const percentage = totalControls > 0 ? Math.round((implementedControls / totalControls) * 100) : 0;

          setFrameworkStats({
            total: totalControls,
            implemented: implementedControls,
            percentage: percentage
          });

          setControlsList(controls);
        }
      } catch (error) {
        console.error("Error computing framework stats:", error);
      }
    };
    computeStats();
  }, [isPage, frameWorkId, OrganizationId, controlsListData]);

  // Pre-initialize AI service when control page opens
  useEffect(() => {
    const preInitializeAI = async () => {
      if (isPage === "control" && open) {
        let retries = 3;
        while (retries > 0) {
          try {
            console.log("🚀 Pre-initializing AI service...");
            const isAvailable = await azureAiService.initialize();
            setAiServiceStatus(isAvailable);
            if (isAvailable) {
              console.log("✅ AI service pre-initialized successfully");
              // Add a small delay to ensure initialization is complete
              setTimeout(() => {
                console.log("⏱️ AI service initialization delay completed");
              }, 500);
              break;
            } else {
              console.log("⚠️ AI service not available during pre-initialization");
              retries--;
              if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
          } catch (error) {
            console.warn("⚠️ AI service pre-initialization failed:", error);
            setAiServiceStatus(false);
            retries--;
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
      }
    };

    preInitializeAI();
  }, [isPage, open]);

  const [hasAnyEditMode, setHasAnyEditMode] = useState(false);
  const [aiServiceStatus, setAiServiceStatus] = useState(false);

  const StyledButton = styled(Button)(({ theme }) => ({
    border: "2px dashed #BDBDBD",
    color: "#9E9E9E",
    borderColor: "#BDBDBD",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "240px",
    width: "240px",
    padding: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#e8e8e8",
      borderColor: "#757575",
    },
  }));

  const handleSave = () => {
    if (childRef.current) {
      childRef.current.saveData();
    }
  };

  const handleEditAll = () => {
    if (onEditAll) {
      onEditAll();
    } else if (childRef.current && childRef.current.handleEditAll) {
      childRef.current.handleEditAll();
    }
  };

  /**
   * CONTROL EDIT BUTTON PERMISSION LOGIC:
   * 
   * The button visibility is controlled based on user roles and the purpose of each edit mode:
   * 
   * 1. EDIT ALL BUTTON:
   *    - Visible: Tech Admin users only
   *    - Reason: Allows editing Control Details which contain framework-level standardized 
   *              information that affects all organizations using this framework
   *    - Impact: Changes can have system-wide implications requiring technical expertise
   * 
   * 2. EDIT BUTTON:
   *    - Visible: All users
   *    - Reason: Allows editing only Implementation and Attachments (organization-specific data)
   *    - Impact: Changes are isolated to the user's organization and don't affect framework standards
   * 
   * This ensures proper access control while allowing users to customize their implementation.
   */
  const canSeeEditAllButton = () => {
    return hasTechAdminAccess();
  };

  const canSeeEditButton = () => {
    // All users can see the Edit button as it only affects organization-specific data
    return true;
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else if (childRef.current && childRef.current.handleEdit) {
      childRef.current.handleEdit();
    }
  };

  const handleEditModeChange = (hasEditMode) => {
    setHasAnyEditMode(hasEditMode);
  };

  const handleSaveAsDraft = () => {
    const buttonTextValue = "saveAsDraft";
    if (childRef.current) {
      childRef.current.saveData(buttonTextValue);
    }
  };

  const handleSaveAsSubmissionApproval = () => {
    const buttonSaveTextValue = "saveAsSubmissionApproval";
    if (childRef.current) {
      childRef.current.saveData(buttonSaveTextValue);
    }
  };

  const handleReject = () => {
    const buttonRejectValue = "saveAsRejectValue";
    if (childRef.current) {
      childRef.current.saveData(buttonRejectValue);
    }
  };

  const handleResendEmail = async () => {
    if (!editItems?.id) {
      toast.error("No email log ID found");
      return;
    }

    setResending(true);
    try {
      // Import the API function dynamically
      const { resendEmailLog } = await import("../../../Api/SystemEmailLogs");
      
      const response = await resendEmailLog(editItems.id);
      
      if (response.status === 200 || response.status === 201) {
        toast.success("Email resent successfully");
        // Close modal and refresh the list if needed
        if (onClose) {
          onClose();
        }
      } else {
        toast.error(response.data?.error || "Failed to resend email");
      }
    } catch (error) {
      console.error("Failed to resend email:", error);
      toast.error(error.response?.data?.error || "Failed to resend email");
    } finally {
      setResending(false);
    }
  };

  const handleApprove = () => {
    const buttonApproveValue = "saveAsApproveValue";
    if (childRef.current) {
      childRef.current.saveData(buttonApproveValue);
    }
  };

  const handleReview = () => {
    if (childRef.current) {
      childRef.current.navigated();
    }
  };

  /**
   * Extract contextual information for the chatbot
   * @returns {Object} Context object with control and framework data
   */
  const getChatbotContext = () => {
    // Defensive handling with safe defaults
    const safeEditItems = editItems || {};
    const safeFrameworkData = frameworkData || {};
    const safeFrameworkStats = frameworkStats || {};
    const safeControlsList = controlsList || [];
    
    // Safely compute control position
    let currentPosition = null;
    if (safeControlsList.length > 0 && safeEditItems?.id) {
      const foundIndex = safeControlsList.findIndex(c => c.id === safeEditItems.id);
      currentPosition = foundIndex >= 0 ? foundIndex + 1 : null;
    }
    
    return {
      // Multi-control context structure
      controlId: safeEditItems?.id || null,
      controlTitle: safeEditItems?.clause || safeEditItems?.name || safeEditItems?.title || null,
      controlData: safeEditItems,
      frameworkData: safeFrameworkData,
      pageType: isPage,
      userAction: flag,
      frameworkStats: safeFrameworkStats,
      controlPosition: {
        current: currentPosition,
        total: safeControlsList.length || 0
      }
    };
  };

  const handleAIToggle = () => {
    // Only allow AI toggle for control pages
    if (isPage === "control") {
      setShowAIColumn(!showAIColumn);
    }
  };

  const CompactFrameworkHeader = ({ editItems }) => {
    // Determine progress color based on percentage
    const getProgressColor = (percentage) => {
      if (percentage >= 80) return '#10B981'; // Green
      if (percentage >= 50) return '#F59E0B'; // Orange/Yellow
      return '#EF4444'; // Red
    };

    const progressColor = getProgressColor(frameworkStats.percentage);
    const progressBgColor = progressColor === '#10B981' ? '#ECFDF5' : 
                           progressColor === '#F59E0B' ? '#FFFBEB' : '#FEF2F2';

    return (
    <Box sx={{
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: '8px 12px',
      mb: 2
    }}>
      {/* Three-column layout with visual separators - 2 lines maximum */}
      <Box sx={{ 
        display: 'flex', 
        gap: '8px',
        width: '100%'
      }}>
        {/* Column 1: Control Navigation with Previous/Current/Next - 2 lines max */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          alignItems: 'flex-start',
          justifyContent: 'center',
          borderRight: '1px solid #E5E7EB',
          px: 1,
          py: 0.5
        }}>
          {/* Line 1: Previous + Clause No | Current Counter | Next + Clause No */}
          <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 1,
            flexWrap: 'nowrap'
          }}>
            {/* Previous Control */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
              gap: 0.5,
              flex: 1
            }}>
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                color: '#6B7280', 
                fontWeight: 500,
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                Previous
              </Typography>
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                color: '#1F2937', 
                fontWeight: 600,
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                {(() => {
                  const idx = controlsList.findIndex(c => c.id === editItems?.id);
                  return idx > 0 ? (controlsList[idx - 1].clause || '-') : '-';
                })()}
              </Typography>
            </Box>
            
            {/* Empty center space for alignment */}
            <Box sx={{ flex: 0, minWidth: '80px' }} />
            
            {/* Next Control */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              justifyContent: 'flex-end'
            }}>
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                color: '#6B7280', 
                fontWeight: 500,
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                Next
            </Typography>
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                color: '#1F2937', 
                fontWeight: 600,
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                {(() => {
                  const idx = controlsList.findIndex(c => c.id === editItems?.id);
                  return idx >= 0 && idx < controlsList.length - 1 ? (controlsList[idx + 1].clause || '-') : '-';
                })()}
            </Typography>
            </Box>
          </Box>

          {/* Line 2: Previous Name | Next Name */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
            gap: 1
          }}>
            {/* Previous Control Name */}
            <Box sx={{ 
              flex: 1,
              textAlign: 'left'
            }}>
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                color: '#4B5563', 
                fontWeight: 700,
                lineHeight: 1.1,
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                {(() => {
                  const idx = controlsList.findIndex(c => c.id === editItems?.id);
                  return idx > 0 ? (controlsList[idx - 1].name || '-') : '-';
                })()}
              </Typography>
            </Box>
            
            {/* Empty center space for alignment */}
            <Box sx={{ flex: 0, minWidth: '80px' }} />
            
            {/* Next Control Name */}
            <Box sx={{ 
              flex: 1,
              textAlign: 'right'
            }}>
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                color: '#4B5563', 
                fontWeight: 700,
                lineHeight: 1.1,
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                {(() => {
                  const idx = controlsList.findIndex(c => c.id === editItems?.id);
                  return idx >= 0 && idx < controlsList.length - 1 ? (controlsList[idx + 1].name || '-') : '-';
                })()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Column 2: Framework Information */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          gap: '4px',
          alignItems: 'flex-start',
          justifyContent: 'center',
          borderRight: '1px solid #E5E7EB',
          px: 1,
          py: 0.5
        }}>
          {/* Line 1: Logo, Acronym, Year Badge, and Framework Details (Right-aligned) */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* Left: Logo, Acronym, and Year Badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Logo */}
              <Box sx={{
                '@media (min-resolution: 1.25dppx)': {
                  display: 'none'
                }
              }}>
                {frameworkData?.file?.sas_url && (
                  <img
                    src={frameworkData.file.sas_url}
                    alt={frameworkData.name}
                    style={{ height: '36px', width: 'auto' }}
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                )}
              </Box>
              
              {/* Framework Acronym */}
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                fontWeight: 700, 
                color: '#1F2937',
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                {frameworkData?.name_acronym || frameworkData?.name || 'FRAMEWORK'}
            </Typography>
              
              {/* Year Badge */}
              <Box sx={{ 
                backgroundColor: '#F3F4F6', 
                color: '#374151', 
                border: '1px solid #D1D5DB',
                px: 1.5, 
                py: '2px', 
                borderRadius: '4px', 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                fontWeight: 600,
                minWidth: '50px',
                textAlign: 'center',
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                {new Date().getFullYear()}
              </Box>
            </Box>
            
            {/* Right: Framework Name and Issuer Badge (Stacked) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                fontWeight: 700, 
                color: '#1F2937', 
                textAlign: 'right',
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                {frameworkData?.name || 'Framework Name'}
            </Typography>
              <Box sx={{ 
                backgroundColor: '#E0F2FE', 
                color: '#0369A1', 
                px: 1.5, 
                py: '2px', 
                borderRadius: '8px', 
                fontSize: { xs: '9px', sm: '10px', md: '11px' }, 
                fontWeight: 600, 
                whiteSpace: 'nowrap',
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '9px'
                }
              }}>
                {frameworkData?.framework_org?.name || 'Issuing Authority'}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Column 3: Progress Information (No Background) */}
        <Box sx={{ 
          flex: 1,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          px: 1,
          py: 0.5
        }}>
          <Box sx={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            {/* Line 1: Progress Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* Left: Shield Icon + Progress Label */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: '16px', 
                  height: '16px', 
                  position: 'relative',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  {/* Shield with four quadrants - top-left and bottom-right are darker */}
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '50%',
                      height: '50%',
                      backgroundColor: progressColor,
                      borderTopLeftRadius: '2px'
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: '50%',
                      height: '50%',
                      backgroundColor: progressColor,
                      borderBottomRightRadius: '2px'
                    }
                  }}>
                    {/* Top-right quadrant (lighter) */}
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '50%',
                      height: '50%',
                      backgroundColor: `${progressColor}40`,
                      borderTopRightRadius: '2px'
                    }} />
                    {/* Bottom-left quadrant (lighter) */}
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '50%',
                      height: '50%',
                      backgroundColor: `${progressColor}40`,
                      borderBottomLeftRadius: '2px'
                    }} />
                  </Box>
                </Box>
                <Typography sx={{ 
                  fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                  fontWeight: 600, 
                  color: progressColor,
                  '@media (min-resolution: 1.25dppx)': {
                    fontSize: '10px'
                  }
                }}>
                  Progress
            </Typography>
              </Box>
              
              {/* Right: Controls Count */}
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                color: progressColor, 
                fontWeight: 500,
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                {frameworkStats.implemented} of {frameworkStats.total} controls
            </Typography>
          </Box>
            
            {/* Line 2: Progress Bar and Percentage */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                flex: 1,
                height: '8px', 
                backgroundColor: `${progressColor}20`, 
                borderRadius: '4px', 
                overflow: 'hidden' 
              }}>
                <Box sx={{ 
                  width: `${frameworkStats.percentage}%`, 
                  height: '100%', 
                  backgroundColor: progressColor, 
                  borderRadius: '4px' 
                }} />
              </Box>
              <Typography sx={{ 
                fontSize: { xs: '10px', sm: '11px', md: '12px' }, 
                fontWeight: 700, 
                color: progressColor, 
                minWidth: '35px',
                textAlign: 'right',
                '@media (min-resolution: 1.25dppx)': {
                  fontSize: '10px'
                }
              }}>
                {frameworkStats.percentage}%
              </Typography>
            </Box>
          </Box>
        </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="switch-account-dialog-title"
      maxWidth={false}
      PaperProps={{
        sx: {
          width: (layoutMode === 'extended' || layoutMode === 'enhanced') ? '85vw' : '70vw',
          height: (layoutMode === 'extended' || layoutMode === 'enhanced') ? "90vh" : "auto",
          maxHeight: (layoutMode === 'extended' || layoutMode === 'enhanced') ? '90vh' : '90vh',
          borderRadius: "8px",
          border: "1px solid #E5E7EB",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 10px 25px 0px rgba(0, 0, 0, 0.15)",
          overflow: (layoutMode === 'extended' || layoutMode === 'enhanced') ? 'hidden' : 'visible',
          display: 'flex',
          flexDirection: 'column'
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          fontSize: 24,
          color: "#1F2937",
          borderBottom: "1px solid #E5E7EB",
          backgroundColor: "#F9FAFB",
          flexShrink: 0,
          position: layoutMode === 'basic' ? 'sticky' : 'static',
          top: 0,
          zIndex: 1
        }}
      >
        
        {/* Left: Close button and AI Toggle - Reduced spacing */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={onClose} aria-label="close" edge="start">
            <CloseIcon />
          </IconButton>
          
          {/* Focus Mode switch - only for controls with permission */}
          {isPage === "control" && hasMenuPermission('control') && (
            <Box
              onClick={() => {
                const framework_id = frameWorkId || editItems?.framework_id;
                // Handle both Control object (has 'id') and FCO object (has 'control_id')
                const control_id = editItems?.control_id || editItems?.id;
                const organization_id = editItems?.organization_id || OrganizationId;
                navigate(
                  `/focus-control-center?framework_id=${framework_id}&control_id=${control_id}&organization_id=${organization_id}`,
                  { state: { from: `${location.pathname}${location.search}` } },
                );
              }}
              sx={{
                ml: 1.5,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                backgroundColor: '#EEF2FF',
                border: '1px solid #C7D2FE',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { 
                  backgroundColor: '#DBEAFE',
                  borderColor: '#93C5FD',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            >
              <FullscreenIcon sx={{ fontSize: 28, color: '#4F46E5' }} />
              <Box>
                <Typography sx={{ 
                  fontWeight: 600, 
                  fontSize: 14, 
                  color: '#4F46E5',
                  lineHeight: 1.2
                }}>
                  Focus Mode
                </Typography>
                <Typography sx={{ 
                  fontSize: 12, 
                  color: '#6B7280',
                  lineHeight: 1.3,
                  mt: 0.25
                }}>
                  Get a full screen view, have more control in Focus Mode
                </Typography>
              </Box>
            </Box>
          )}

        </Box>
        
        {/* Center: Title */}
        <Typography sx={{ 
          fontWeight: 600, 
          fontSize: 24, 
          color: "#1F2937",
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)'
        }}>
        {title}
        </Typography>
        
        {/* Right: Navigation buttons (only for control page) */}
        {isPage === "control" ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Editable Control Counter */}
            <Box 
        sx={{
          display: 'flex',
                alignItems: 'center', 
                gap: 0.5,
                backgroundColor: '#F3F4F6',
                border: '1px solid #D1D5DB',
                borderRadius: '4px',
                px: 1.5,
                py: 0.5
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
            >
              <Typography 
                sx={{ 
                  fontSize: '12px', 
                  color: '#374151', 
                  fontWeight: 500 
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                Control
              </Typography>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={(() => {
                  const currentIdx = controlsList.findIndex(c => c.id === editItems?.id);
                  return currentIdx >= 0 ? currentIdx + 1 : 1;
                })()}
                onChange={(e) => {
                  const inputValue = parseInt(e.target.value);
                  if (inputValue && inputValue >= 1 && inputValue <= frameworkStats.total) {
                    const newIndex = inputValue - 1;
                    if (newIndex >= 0 && newIndex < controlsList.length) {
                      const targetControl = controlsList[newIndex];
                      if (targetControl) {
                        if (navigateToControl) {
                          navigateToControl(targetControl);
                        }
                      }
                    }
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent edit mode activation
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onKeyUp={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  // Prevent edit mode activation
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onMouseUp={(e) => {
                  e.stopPropagation();
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                }}
                onBlur={(e) => {
                  e.stopPropagation();
                  const inputValue = parseInt(e.target.value);
                  if (!inputValue || inputValue < 1 || inputValue > frameworkStats.total) {
                    // Reset to current value if invalid
                    const currentIdx = controlsList.findIndex(c => c.id === editItems?.id);
                    e.target.value = currentIdx >= 0 ? currentIdx + 1 : 1;
                  }
                }}
                style={{
                  width: '45px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#1F2937',
                  textAlign: 'center',
                  outline: 'none',
                  padding: '2px 4px',
                  cursor: 'text'
                }}
              />
              <Typography 
                sx={{ 
                  fontSize: '12px', 
                  color: '#374151', 
                  fontWeight: 500 
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                of {frameworkStats.total}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              disabled={(() => {
                const currentIdx = controlsList.findIndex(c => c.id === editItems?.id);
                return currentIdx <= 0;
              })()}
              onClick={(e) => {
                e.stopPropagation();
                const currentIdx = controlsList.findIndex(c => c.id === editItems?.id);
                if (currentIdx > 0) {
                  const prevControl = controlsList[currentIdx - 1];
                  if (prevControl) {
                    if (navigateToControl) {
                      navigateToControl(prevControl);
                    }
                  }
                }
              }}
            sx={{ 
                borderColor: '#D1D5DB',
                color: '#6B7280',
                textTransform: 'none',
                fontSize: '12px',
                px: 2,
                py: 0.5,
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F9FAFB'
                },
                '&:disabled': {
                  borderColor: '#E5E7EB',
                  color: '#9CA3AF',
                  backgroundColor: '#F9FAFB'
                }
              }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={(() => {
                const currentIdx = controlsList.findIndex(c => c.id === editItems?.id);
                return currentIdx >= controlsList.length - 1;
              })()}
              onClick={(e) => {
                e.stopPropagation();
                const currentIdx = controlsList.findIndex(c => c.id === editItems?.id);
                if (currentIdx >= 0 && currentIdx < controlsList.length - 1) {
                  const nextControl = controlsList[currentIdx + 1];
                  if (nextControl) {
                    if (navigateToControl) {
                      navigateToControl(nextControl);
                    }
                  }
                }
              }}
              sx={{
                borderColor: '#D1D5DB',
                color: '#6B7280',
                textTransform: 'none',
                fontSize: '12px',
                px: 2,
                py: 0.5,
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F9FAFB'
                },
                '&:disabled': {
                  borderColor: '#E5E7EB',
                  color: '#9CA3AF',
                  backgroundColor: '#F9FAFB'
                }
              }}
            >
              Next
            </Button>

          </Box>
        ) : (
          <IconButton aria-label="more options" style={{ visibility: "hidden" }}>
            <MoreHorizIcon />
          </IconButton>
        )}
      </DialogTitle>
      {extraBottomContent}
      <DialogContent sx={{ 
        p: (layoutMode === 'extended' || layoutMode === 'enhanced') ? 0 : 3, 
        backgroundColor: (isPage === "control" || isPage === "control-implementation-template") && layoutMode === 'extended' ? "#F9FAFB" : "#FFFFFF", 
        flex: 1, 
        overflow: 'auto',
        display: layoutMode === 'basic' ? 'flex' : 'block',
        flexDirection: layoutMode === 'basic' ? 'column' : 'initial',
        alignItems: layoutMode === 'basic' ? 'center' : 'initial',
        justifyContent: layoutMode === 'basic' ? 'flex-start' : 'initial',
        minHeight: (layoutMode === 'extended' || layoutMode === 'enhanced') ? 0 : (layoutMode === 'basic' ? '400px' : 'auto')
      }}>
        {isPage === "control" && layoutMode === 'extended' ? (
          <Box sx={{ p: '16px 16px 0 16px', flexShrink: 0 }}>
            <CompactFrameworkHeader editItems={editItems} />
          </Box>
        ) : isPage === "control-implementation-template" && layoutMode === 'extended' ? (
          <Box sx={{ p: '16px 16px 0 16px', flexShrink: 0 }}>
            {/* Framework header for control implementation template - can be customized if needed */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: "16px", fontWeight: "600", color: "#374151" }}>
                {editItems?.framework_name || 'Framework'}
              </Typography>
            </Box>
          </Box>
        ) : isPage === "email notifications" ? (
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#6B7280", mb: 2, textAlign: 'center' }}>{subTitle}</Typography>
          </Box>
        ) : layoutMode === 'basic' ? (
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#6B7280", mb: 2, textAlign: 'center' }}>{subTitle}</Typography>
          </Box>
        ) : null}

        <Suspense fallback={<ModalLoadingFallback />}>
          <Box sx={{ 
            p: (layoutMode === 'extended' || layoutMode === 'enhanced') ? 0 : 0, 
            flex: (layoutMode === 'extended' || layoutMode === 'enhanced') ? 1 : 'none', 
            display: (layoutMode === 'extended' || layoutMode === 'enhanced') ? 'flex' : 'block', 
            flexDirection: (layoutMode === 'extended' || layoutMode === 'enhanced') ? 'column' : 'initial',
            width: layoutMode === 'basic' ? '100%' : 'auto',
            maxWidth: (layoutMode === 'basic' && isPage !== 'risk_assessment' && isPage !== 'risk_remediation')
              ? '600px'
              : (layoutMode === 'basic' ? '100%' : 'none'),
          // Ensure upload containers match field width in basic mode
          '& .uploadContainer': layoutMode === 'basic' ? {
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          } : {},
          '& .uploadSubContainer': layoutMode === 'basic' ? {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          } : {},

          // Style uploaded file display
          '& .uploadSubContainer .uploaded-file-display': layoutMode === 'basic' ? {
            width: '100%',
            height: '120px',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: '#FAFAFA',
            position: 'relative',
            '&:hover': {
              backgroundColor: '#F0F0F0'
            }
          } : {}
        }}>
            {isPage === "email notifications" ? (
              <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Time</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                      {editItems?.created_at ? new Date(editItems.created_at).toLocaleString() : '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Recipient</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                      {(editItems?.to_name || '') + (editItems?.to_email ? ` <${editItems.to_email}>` : '')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>From</Typography>
                    <Typography sx={{ fontSize: '14px' }}>{editItems?.from_email || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Template</Typography>
                    <Typography sx={{ fontSize: '14px' }}>{(editItems?.template_name || '-') + (editItems?.template_id ? ` (${editItems.template_id})` : '')}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Context</Typography>
                    <Typography sx={{ fontSize: '14px' }}>{editItems?.context_type || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Status</Typography>
                    <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: '999px', backgroundColor: '#f3f4f6', fontSize: '12px', fontWeight: 700 }}>
                      {String(editItems?.status || '-').charAt(0).toUpperCase() + String(editItems?.status || '').slice(1).toLowerCase()}
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Message ID</Typography>
                    <Typography sx={{ fontSize: '14px' }}>{editItems?.provider_message_id || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Correlation ID</Typography>
                    <Typography sx={{ fontSize: '14px' }}>{editItems?.correlation_id || '-'}</Typography>
                  </Box>
                  {editItems?.error_message && (
                    <Box>
                      <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Error</Typography>
                      <Typography sx={{ fontSize: '14px' }}>{editItems.error_message}</Typography>
                    </Box>
                  )}
                  {editItems?.response_excerpt && (
                    <Box>
                      <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Response</Typography>
                      <Typography sx={{ fontSize: '14px' }}>{editItems.response_excerpt}</Typography>
                    </Box>
                  )}
                  {editItems?.request_payload && (
                    <Box>
                      <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Payload</Typography>
                      <Box component="pre" sx={{ 
                        mt: 0.5, 
                        p: 1.25, 
                        border: '1px solid #e5e7eb', 
                        borderRadius: 1, 
                        background: '#f9fafb', 
                        maxHeight: 300, 
                        overflow: 'auto', 
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {(() => {
                          try {
                            const payload = typeof editItems.request_payload === 'string' 
                              ? JSON.parse(editItems.request_payload) 
                              : editItems.request_payload;
                            return JSON.stringify(payload, null, 2);
                          } catch (e) {
                            return editItems.request_payload;
                          }
                        })()}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : isPage === "country" ? (
              <Box>
                <CountryModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getCountryAllData={getCountryAllData}
                  editItems={editItems}
                />
              </Box>
            ) : isPage === "group & roles" ? (
              <Box>
                <GroupListModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  fetchGroupData={fetchGroupData}
                  editItems={editItems}
                  groupTypes={groupTypes}
                />
              </Box>
            ) : isPage === "group-type" ? (
              <Box>
                <GroupTypeModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : isPage === "organization" ? (
              <Box>
                <OrganizationModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  fetchGroupData={fetchGroupData}
                  editItems={editItems}
                  groupTypes={groupTypes}
                  getAllData={getAllData}
                  selectedOrg={selectedOrg}
                  setUpdatePinBarData={setUpdatePinBarData}
                  fetchOrgData
                  showUploadImage={"false"}
                  onSubmit={getAllData}
                  prefillData={selectedOrg}
                  // addOrganizationStatus={addOrganizationStatus}
                />
              </Box>
            ) : isPage === "framework" ? (
              <Box>
                <FrameworkModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : isPage === "control" ? (
              <Box>
                <ControlModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  frameWorkId={frameWorkId}
                  onEditAll={handleEditModeChange}
                  editModeType={flag === "edit_user_implement" ? "user_implement" : "full"}
                  onSaveComplete={onSaveComplete}
                  onDataRefresh={onDataRefresh}
                  showAIColumn={showAIColumn}
                  onAIToggle={handleAIToggle}
                />
              </Box>
            ) : isPage === "risk_assessment" ? (
              <Box sx={{ width: '100%' }}>
                <RiskAssessmentModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  //frameWorkId={frameWorkId}
                />
              </Box>  
            ) : isPage === "risk_remediation" ? (
              <Box sx={{ width: '100%' }}>
                <RiskRemediationModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  userList={userList}
                />
              </Box>
            ) : isPage === "policy center" ? (
              <Box>
                <PolicyCenterModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  frameWorkId={frameWorkId}
                  relatedModelType={relatedModelType}
                  relatedModelId={relatedModelId}
                />
              </Box>
            ) : (isPage === "software asset" || isPage === "Hardware Asset" )? (
              <Box>
                <AssetManagementModal
                  flag={flag}
                  open={open}
                  ref={childRef}
                  onClose={onClose}
                  getAssetManagementAllData={getAssetManagementAllData}
                  editItems={editItems}
                  currentActiveTab={currentActiveTab}
                  vendorNames={vendorNames}
                  fullUserNames={fullUserNames}
                  existingSoftwareAssets={existingSoftwareAssets}
                />
              </Box>
            ) : isPage === "training" ? (
              <Box>
                <TrainingModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  
                  
                />
              </Box>
            ) : isPage === "form" ? (
              <Box>
                <FormStandardModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : isPage === "training attendance" ? (
              <Box>
                <TrainingAttendanceModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  
                  
                />
              </Box>   
            ): isPage === "vendor" ? (
              <Box>
                <VendorModal
                  flag={flag}
                  open={open}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  vendorAccount={vendorAccount}
                />
              </Box>
            ): isPage === "account" ? (
              <Box>
                <VendorAccountModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  vendorNames={vendorNames}
                  fullUserNames={fullUserNames}
                />
              </Box>
            ): isPage === "data inventory" ? (
              <Box>
                <DataInventoryModal
                  flag={flag}
                  open={open}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  fullUserNames={fullUserNames}
                />
              </Box>    
            ): isPage === "policy" ? (
              <Box>
              <PolicyModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  frameWorkId={frameWorkId}
                />
              </Box>
            ): isPage === "users" ? (
              <Box>
              <UsersModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  getActiveTabID={getActiveTabID}
                />
              </Box>
            ) : isPage === "projects" ? (
              <Box>
              <ProjectModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  getActiveTabID={getActiveTabID}
                />
              </Box>
            ) : isPage === "framework-organization" ? (
              <Box>
                <FrameworkOrganizationModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : isPage === "policy-template" ? (
              <Box>
                <PolicyTemplateModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : isPage === "control-implementation-template" ? (
              <Box>
                {(flag === "edit1" || flag === "view1") && ( 
                  // edit and view are the flags for the control implementation template page - 
                  // temporary removed by changed edit to edit1 and view to view1  
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <Box sx={{ 
                        fontSize: '0.9em',
                        color: '#666',
                        fontWeight: 500,
                        letterSpacing: '0.5px'
                      }}>
                        Control Information
                      </Box>
                      <Box sx={{ 
                        flex: 1,
                        height: '1px',
                        backgroundColor: '#e0e0e0'
                      }} />
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '4px',
                      padding: '16px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px'
                    }}>
                      <Box sx={{ 
                        fontWeight: 'bold',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}>{editItems?.clause || '-'}</Box>
                      <Box sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}>{editItems?.control_name || '-'}</Box>
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: '4px', 
                        fontSize: '0.9em'
                      }}>
                        <Chip 
                          label={editItems?.category || '-'} 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#e3f2fd',
                            fontSize: '0.85em'
                          }}
                        />
                        <Chip 
                          label={editItems?.domain || '-'} 
                          size="small"
                          sx={{ 
                            backgroundColor: '#f3e5f5',
                            fontSize: '0.85em'
                          }}
                        />
                        <Chip 
                          label={editItems?.mandatory ? 'SHALL' : 'SHOULD'} 
                          size="small"
                          sx={{ 
                            backgroundColor: editItems?.mandatory ? '#ffebee' : '#e8f5e9',
                            color: editItems?.mandatory ? '#d32f2f' : '#2e7d32',
                            fontWeight: 'bold',
                            fontSize: '0.85em'
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
                <ControlImplementationTemplateModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  layoutMode={layoutMode}
                />
              </Box>
            ) : isPage === "risk" ? (
              <Box>
                <RiskModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : (isPage === "system-default-fields" || isPage === "resource-defaults") ? (
              <Box>
                <ResourceDefaultsModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  entityType={entityType}
                  organizationScope={organizationScope}
                />
              </Box>
            ) : isPage === "ip_register" ? (
              <Box>
                <IPRegisterFormModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                  config={ipRegisterConfig}
                  onSave={onSave}
                />
              </Box>
            ) : isPage === "grant-entitlement" ? (
              <Box>
                <GrantEntitlementModal
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : isPage === "policy-review-start" ? (
              <Box>
                <ReviewWithZavvyModal
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : isPage === "entitlement-code" ? (
              <Box>
                <EntitlementCodeModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : isPage === "knowledge-library" ? (
              <Box>
                <KnowledgeLibraryModal
                  flag={flag}
                  ref={childRef}
                  onClose={onClose}
                  getAllData={getAllData}
                  editItems={editItems}
                />
              </Box>
            ) : null}
          </Box>
        </Suspense>
      </DialogContent>

      <DialogActions 
        dividers
        sx={{
          flexShrink: 0,
          justifyContent: layoutMode === 'basic' ? 'flex-end' : 'space-between',
          p: 2,
          borderTop: "1px solid #E5E7EB",
          backgroundColor: "#FFFFFF"
        }}
      >
        <Button onClick={onClose} color="primary" className="global-text-button">
          Cancel
        </Button>
        
        {/* Resend button for email notifications in view mode */}
        {isPage === "email notifications" && flag === "view" && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleResendEmail}
            disabled={resending}
            sx={{ mr: 1 }}
          >
            {resending ? 'Resending...' : 'Resend'}
          </Button>
        )}
        
        {/* Control page specific buttons */}
        {isPage === "control" && (
          <>
            {/* 
              EDIT BUTTONS - Only shown in view mode
              
              Two distinct edit modes are provided to maintain proper access control:
              
              1. Edit All (Tech Admin only): 
                 - Allows editing Control Details + Implementation + Attachments
                 - Control Details contain framework-level data that affects all organizations
                 - Requires technical expertise to maintain system-wide consistency
              
              2. Edit (All users):
                 - Allows editing Implementation + Attachments only (organization-specific data)
                 - Control Details remain read-only to protect framework integrity
                 - Users can customize their implementation without affecting the standard
            */}
            {flag === "view" && (
              <>
                {/* Edit All button - Tech Admin only due to system-wide impact */}
              {canSeeEditAllButton() && (
                <Button
                    onClick={handleEditAll}
                    className="global-primary-button"
                    sx={{ mr: 1 }}
                >
                  Edit All
                </Button>
              )}
                
                {/* Edit button - All users (restricted to organization-specific data) */}
              {canSeeEditButton() && (
                                  <Button
                    onClick={handleEdit}
                    className="global-primary-button"
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
              )}
            </>
          )}
          
            {/* 
              SAVE BUTTON - Only shown when in edit mode
              
              Appears when user is in either:
              - "editall" mode (Tech Admin editing all sections)
              - "edit_user_implement" mode (All users editing implementation only)
              - "add" mode (Creating new control)
            */}
            {(flag === "editall" || flag === "edit_user_implement" || flag === "add") && (
              <Button
                onClick={handleSave}
                className="global-primary-button"
              >
                Save
              </Button>
            )}
          </>
        )}
        
        {/* Handle non-control pages */}
        {flag === "view" && isPage !== "control" && (
          <>
            {/* Edit All button - Tech Admin only for non-control pages */}
            {canSeeEditAllButton() && onEditAll && (
              <Button
                onClick={handleEditAll}
                className="global-primary-button"
                sx={{ mr: 1 }}
              >
                Edit All
              </Button>
            )}
            
            {/* Edit button - All users for non-control pages */}
            {onEdit && (
            <Button
              onClick={onEdit}
                className="global-primary-button"
                sx={{ mr: 1 }}
            >
              Edit
            </Button>
          )}
          </>
        )}
          {isPage === "policy center" &&
            (flag === "edit" || flag === "view") && (
              <Button
                variant="contained"
                onClick={handleReview}
              className="saveModal"
              >
               {["Approved"].includes(editItems?.status) ? "Preview" :"Review"} 
              </Button>
            )}
          {isPage === "policy center" &&
            !["Approved", "Pending Approval"].includes(editItems?.status)  &&
            (
              <Button
                variant="contained"
                onClick={handleSaveAsDraft}
              className="saveModal"
              >
                Save as Draft
              </Button>
            )}
        {(flag === "add" || flag === "edit" || flag === "editall") && isPage !== "control" && isPage !== "policy center" && flag !== "account" && (
            <Button
              variant="contained"
              onClick={handleSave}
            className="saveModal"
            >
              {isPage === "grant-entitlement"
                ? "Grant"
                : isPage === "policy-review-start"
                  ? "Start review"
                : isPage === "entitlement-code"
                  ? (flag === "add" ? "Create Code" : "Save Changes")
                  : (flag === "add" ? "Add" : "Save")}
            </Button>
          )}
      </DialogActions>

      {/* AI Chatbot - Only show for control pages and when AI is enabled */}
      {isPage === "control" && aiEnabled && (
        <Box sx={{ 
          p: '16px 16px 0 16px', 
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          {/* AI Assistant Card */}
          <ErrorBoundary onError={(error, errorInfo) => {
            console.error('ChatbotAgent Error Boundary caught an error:', error, errorInfo);
            // Optionally, you can log the error to a service or show a user-friendly message
          }} fallback={
            <Box sx={{ 
              p: 1, 
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '12px'
            }}>
              AI Assistant temporarily unavailable
            </Box>
          }>
            <Suspense fallback={
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 2,
                gap: 1
              }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading AI Assistant...
                </Typography>
              </Box>
            }>
              <ChatbotAgent 
                key={editItems?.id || 'default'}
                open={isChatbotOpen}
                onClose={() => setIsChatbotOpen(false)}
                context={getChatbotContext()}
                controlId={editItems?.id || null}
                sessionId={`control-${editItems?.id || 'default'}`}
                enableMultiControl={true}
                maxContextLimit={5}
              />
            </Suspense>
          </ErrorBoundary>
        </Box>
      )}

    </Dialog>
  );
};

export default StandardFormView;
